import { t } from "@lingui/core/macro";
import dayjs from "dayjs";
import type { GetAppointment, GetNotificationSettings } from "@/server-types";
import type { NotificationSettingToggle } from "./NotificationSettingsSwitchList";

export type NotificationLog = {
  trigger: string | null;
  event: string | null;
  message_content: string | null;
  timeSent: string;
  status: string;
  deliveryStatus: "pending" | "delivered" | "seen" | "error";
  notificationType: "sms" | "viber" | "whatsapp" | "email" | null;
  originalType: "sms" | "viber" | "whatsapp" | "email";
  notificationLogId: number;
  status_message?: string | null;
  notificationSettingId?: number | null;
};

export function getNotificationEventGroups() {
  return [
    { events: ["newAppointment"], label: t`Ob potrditvi` },
    { events: ["rescheduled"], label: t`Ob spremembi termina` },
    { events: ["canceled"], label: t`Ob odpovedi` },
    { events: ["noShow"], label: t`Ob neprihodu` },
    {
      events: ["appointmentReminder", "appointmentFollowUp"],
      label: t`Pred/po terminu`,
    },
    { events: ["thankYouVisiting"], label: t`Zahvala za obisk` },
    { events: ["messageSchedule"], label: t`Ročno načrtovana sporočila` },
    { events: ["onlinePaymentReminderSent"], label: t`Povezava za plačilo` },
  ];
}

// Legacy `trigger` values map 1:1 to the new `event` (MessageKind) values
// after the Phase 6 backfill. Matching either field places rows written by the
// new dispatcher (event only) and legacy rows (trigger only) alike.
const LEGACY_TRIGGER_TO_EVENT: Record<string, string> = {
  onConfirmation: "newAppointment",
  onReschedule: "rescheduled",
  onCancelation: "canceled",
  onCancellation: "canceled",
  cronBeforeAppointment: "appointmentReminder",
  cronAfterAppointment: "appointmentFollowUp",
};

export function matchesEvent(
  row: { event: string | null; trigger: string | null },
  events: string[],
): boolean {
  if (row.event && events.includes(row.event)) return true;
  if (row.trigger) {
    const mapped = LEGACY_TRIGGER_TO_EVENT[row.trigger] ?? row.trigger;
    if (events.includes(mapped)) return true;
  }
  return false;
}

export type NotificationSettingDetails =
  GetNotificationSettings["response"]["settings"][number];

export type NotificationScheduleEntry = Pick<
  GetAppointment["response"]["appointment"]["appointmentParts"][number]["customers"][number]["notificationSchedule"][number],
  | "notificationScheduleId"
  | "notificationSettingId"
  | "scheduledAt"
  | "createdAt"
  | "status"
  | "errorCode"
>;

export type BatchConfirmation =
  GetAppointment["response"]["appointment"]["appointmentParts"][number]["customers"][number]["batchConfirmation"];

export type WontSendReason = "savedLate" | "settingChangedLate" | "movedLate";

export type WaitingEvent = "rescheduled" | "canceled" | "noShow";

export type NotificationRowState =
  | { kind: "willSend" }
  | { kind: "sent"; log: NotificationLog }
  | { kind: "sentWithBatch"; confirmedAppointmentStartTime: string }
  | { kind: "failed"; log: NotificationLog }
  | { kind: "scheduled"; sendAt: string }
  | { kind: "sending" }
  | {
      kind: "wontSend";
      reason: WontSendReason;
      sendAt: string;
      decidedAt: string | null;
    }
  | { kind: "canceled" }
  | { kind: "optedOut" }
  | { kind: "skippedInvalid" }
  | { kind: "scheduleError" }
  | { kind: "waitingForEvent"; event: WaitingEvent }
  | { kind: "disabledForAppointment" }
  | { kind: "disabledInSettings" }
  | { kind: "noChannel" }
  | { kind: "notScheduled" }
  | { kind: "notSent" };

export type NotificationRow = {
  key: string;
  label: string;
  detail?: string;
  setting?: NotificationSettingDetails;
  state: NotificationRowState;
  toggle?: { settingId: number; enabled: boolean };
};

export type NotificationRowGroup = {
  label: string;
  rows: NotificationRow[];
};

const APPOINTMENT_SCOPED_EVENTS = new Set([
  "newAppointment",
  "rescheduled",
  "canceled",
  "noShow",
  "appointmentReminder",
  "appointmentFollowUp",
  "thankYouVisiting",
]);

export function isAppointmentScopedSetting(setting: {
  event?: string | null;
  trigger?: string | null;
}): boolean {
  if (setting.event) return APPOINTMENT_SCOPED_EVENTS.has(setting.event);
  return !!setting.trigger;
}

const SCHEDULE_AHEAD_EVENTS = new Set([
  "appointmentReminder",
  "appointmentFollowUp",
  "thankYouVisiting",
]);

function isWaitingEvent(event: string): event is WaitingEvent {
  return event === "rescheduled" || event === "canceled" || event === "noShow";
}

// Mirrors the cron, which only picks up rows due within the last 24 hours.
const CRON_PICKUP_WINDOW_HOURS = 24;

const CRON_HANDLED_STATUSES = new Set([
  "processing",
  "sent",
  "error",
  "invalidated",
]);

function settingEvent(setting: NotificationSettingDetails): string | null {
  if (setting.event) return setting.event;
  if (!setting.trigger) return null;
  return LEGACY_TRIGGER_TO_EVENT[setting.trigger] ?? setting.trigger;
}

function latestScheduleFor(
  settingId: number,
  notificationSchedule: NotificationScheduleEntry[],
): NotificationScheduleEntry | undefined {
  const entries = notificationSchedule.filter(
    (entry) => entry.notificationSettingId === settingId,
  );
  const wasHandled = entries.some((entry) =>
    CRON_HANDLED_STATUSES.has(entry.status),
  );

  // A skip recorded after the cron picked the row up must not hide its outcome.
  return entries
    .filter((entry) => !wasHandled || entry.status !== "skipped")
    .reduce<NotificationScheduleEntry | undefined>(
      (latest, entry) =>
        !latest || entry.notificationScheduleId > latest.notificationScheduleId
          ? entry
          : latest,
      undefined,
    );
}

function wontSendReason(errorCode: string | null): WontSendReason {
  if (errorCode === "PAST_SET") return "settingChangedLate";
  if (errorCode === "RESCH_PAST") return "movedLate";
  return "savedLate";
}

/** State of a setting that has a schedule row but no log row yet. */
export function scheduleState(
  schedule: NotificationScheduleEntry,
  now: Date,
): NotificationRowState {
  switch (schedule.status) {
    case "scheduled": {
      const sendAt = dayjs(schedule.scheduledAt);
      if (sendAt.isAfter(now)) {
        return { kind: "scheduled", sendAt: schedule.scheduledAt };
      }
      const isStale = sendAt.isBefore(
        dayjs(now).subtract(CRON_PICKUP_WINDOW_HOURS, "hours"),
      );
      return isStale ? { kind: "notSent" } : { kind: "sending" };
    }
    case "processing":
      return { kind: "sending" };
    case "skipped":
      return {
        kind: "wontSend",
        reason: wontSendReason(schedule.errorCode),
        sendAt: schedule.scheduledAt,
        decidedAt: schedule.createdAt,
      };
    case "canceled":
      return schedule.errorCode === "RESCH_PAST"
        ? {
            kind: "wontSend",
            reason: "movedLate",
            sendAt: schedule.scheduledAt,
            decidedAt: null,
          }
        : { kind: "canceled" };
    case "invalidated":
      return schedule.errorCode === "OPT_OUT"
        ? { kind: "optedOut" }
        : { kind: "skippedInvalid" };
    case "error":
      return { kind: "scheduleError" };
    default:
      return { kind: "notSent" };
  }
}

export function isFailedLog(log: NotificationLog): boolean {
  return (
    log.deliveryStatus === "error" ||
    log.status === "error" ||
    log.status === "failed" ||
    log.status === "blocked"
  );
}

/**
 * One row per notification the appointment could have produced, grouped by
 * event. A setting that sent something contributes a row per log row (the log
 * is per channel); one that did not contributes a single row carrying the
 * reason it stayed silent.
 */
export function buildNotificationRowGroups({
  settings,
  notificationLog,
  notificationSchedule,
  disabledNotificationSettingIds,
  batchConfirmation = null,
  toggles,
  resolveChannelCount,
  labelForSetting,
  detailForSetting,
  isNewAppointment = false,
  now = new Date(),
}: {
  settings: NotificationSettingDetails[] | undefined;
  notificationLog: NotificationLog[];
  notificationSchedule: NotificationScheduleEntry[];
  disabledNotificationSettingIds: number[];
  batchConfirmation?: BatchConfirmation;
  toggles: NotificationSettingToggle[];
  resolveChannelCount: (setting: NotificationSettingDetails) => number;
  labelForSetting: (setting: NotificationSettingDetails) => string;
  detailForSetting: (setting: NotificationSettingDetails) => string | undefined;
  isNewAppointment?: boolean;
  now?: Date;
}): NotificationRowGroup[] {
  // Nothing has happened yet on an appointment that does not exist, so the
  // create flow lists only what the form will actually send and says no more
  // than that.
  const appointmentSettings = (settings ?? [])
    .filter(isAppointmentScopedSetting)
    .filter(
      (setting) =>
        !isNewAppointment || toggles.some((toggle) => toggle.id === setting.id),
    );

  const emittedLogIds = new Set<number>();

  function logRow(log: NotificationLog, label: string): NotificationRow {
    emittedLogIds.add(log.notificationLogId);
    return {
      key: `log-${log.notificationLogId}`,
      label,
      state: isFailedLog(log) ? { kind: "failed", log } : { kind: "sent", log },
    };
  }

  function rowsForSetting(setting: NotificationSettingDetails) {
    const label = labelForSetting(setting) || `#${setting.id}`;
    const detail = detailForSetting(setting);

    if (isNewAppointment) {
      const toggle = toggles.find((candidate) => candidate.id === setting.id);
      return [
        {
          key: `setting-${setting.id}`,
          label,
          detail,
          setting,
          state: { kind: "willSend" },
          toggle: toggle
            ? { settingId: setting.id, enabled: toggle.enabled }
            : undefined,
        } satisfies NotificationRow,
      ];
    }

    const logs = notificationLog.filter(
      (log) => log.notificationSettingId === setting.id,
    );

    if (logs.length > 0) {
      return logs.map((log) => ({ ...logRow(log, label), detail }));
    }

    const toggle = toggles.find((candidate) => candidate.id === setting.id);
    const schedule = latestScheduleFor(setting.id, notificationSchedule);

    const state = ((): NotificationRowState => {
      if (resolveChannelCount(setting) === 0) return { kind: "noChannel" };
      if (!setting.isEnabled) return { kind: "disabledInSettings" };

      const isOff = toggle
        ? !toggle.enabled
        : disabledNotificationSettingIds.includes(setting.id);
      if (isOff) return { kind: "disabledForAppointment" };

      if (schedule) return scheduleState(schedule, now);

      const event = settingEvent(setting);
      if (event && isWaitingEvent(event)) {
        return { kind: "waitingForEvent", event };
      }
      if (event && SCHEDULE_AHEAD_EVENTS.has(event)) {
        return { kind: "notScheduled" };
      }
      // A customer booked into several classes at once is confirmed once, on
      // one of them.
      if (event === "newAppointment" && batchConfirmation) {
        return {
          kind: "sentWithBatch",
          confirmedAppointmentStartTime: batchConfirmation.startTime,
        };
      }
      return { kind: "notSent" };
    })();

    return [
      {
        key: `setting-${setting.id}`,
        label,
        detail,
        setting,
        state,
        toggle: toggle
          ? { settingId: setting.id, enabled: toggle.enabled }
          : undefined,
      } satisfies NotificationRow,
    ];
  }

  const groups = getNotificationEventGroups()
    .map((group) => {
      const rows = appointmentSettings
        .filter((setting) =>
          matchesEvent(
            { event: setting.event ?? null, trigger: setting.trigger ?? null },
            group.events,
          ),
        )
        .flatMap(rowsForSetting);

      // Rows written before the dispatcher recorded its lineage carry no
      // setting id, so they can only be placed by their event.
      const unattributed = notificationLog
        .filter(
          (log) =>
            log.notificationSettingId == null &&
            matchesEvent(log, group.events),
        )
        .map((log) => logRow(log, group.label));

      return { label: group.label, rows: [...rows, ...unattributed] };
    })
    .filter((group) => group.rows.length > 0);

  // A log row whose setting was deleted, or whose event predates the groups
  // above, still happened — losing it here would recreate the blind spot this
  // list exists to remove.
  const leftover = notificationLog.filter(
    (log) => !emittedLogIds.has(log.notificationLogId),
  );
  if (leftover.length > 0) {
    groups.push({
      label: t`Ostalo`,
      rows: leftover.map((log) =>
        logRow(log, log.event ?? log.trigger ?? t`Neznano obvestilo`),
      ),
    });
  }

  return groups;
}

/**
 * Moves the rows of settings turned off in the notification settings out of
 * their groups, keeping each group's label, so they can be listed compactly.
 */
export function splitOffDisabledInSettings(groups: NotificationRowGroup[]): {
  groups: NotificationRowGroup[];
  disabledGroups: NotificationRowGroup[];
} {
  function isDisabled(row: NotificationRow): boolean {
    return row.state.kind === "disabledInSettings";
  }

  return {
    groups: groups
      .map((group) => ({
        ...group,
        rows: group.rows.filter((row) => !isDisabled(row)),
      }))
      .filter((group) => group.rows.length > 0),
    disabledGroups: groups
      .map((group) => ({ ...group, rows: group.rows.filter(isDisabled) }))
      .filter((group) => group.rows.length > 0),
  };
}
